/**
 * BOXO fix: the product video used to autoplay by itself on mobile when a
 * shopper scrolled/swiped to it. After the theme's "Video style" setting
 * was switched to the sound-enabled version, mobile always requires a tap
 * instead (and tapping used to jump the video into a different, fullscreen
 * format).
 *
 * Sound-with-autoplay isn't something any site can do - phone browsers
 * block audio from starting without a tap, no matter how it's coded. So
 * this restores the original behaviour instead: on mobile, whichever slide
 * is the currently-selected one autoplays automatically, muted, in the same
 * square box the poster image uses - no tap, no sound, no fullscreen jump.
 * This mirrors exactly how the desktop version of this same video already
 * autoplays (muted) when its slide is selected.
 *
 * It only touches this one interaction; it does not modify or replace any
 * existing theme behaviour (desktop autoplay, thumbnails, zoom, etc.).
 */
(function () {
  // Same breakpoint the theme itself uses to show/hide the mobile vs.
  // desktop video markup ("medium-up--hide" / "small--hide").
  function isMobileWidth() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  var playing = new WeakMap(); // slide element -> the muted clone playing in it

  function hideTrigger(slide) {
    var trigger = slide.querySelector('.product-video-trigger--mp4');
    if (trigger) trigger.classList.add('hide');
  }

  function stopVideo(slide) {
    var clone = playing.get(slide);
    if (!clone) return;
    if (clone.parentNode) clone.parentNode.removeChild(clone);
    playing.delete(slide);
  }

  function startVideo(slide) {
    if (!isMobileWidth() || playing.has(slide)) return;

    var original = slide.querySelector('video.product-video-mp4-sound');
    var mediaBox = slide.querySelector('.product-image-main');
    if (!original || !mediaBox) return;

    hideTrigger(slide);

    // Clone rather than reuse the original element, same approach already
    // proven to start this video reliably.
    var clone = original.cloneNode(true);
    clone.removeAttribute('id');
    clone.removeAttribute('controls');
    clone.classList.remove('hide');
    clone.muted = true;
    clone.defaultMuted = true;
    clone.setAttribute('muted', '');
    clone.loop = true;
    clone.style.position = 'absolute';
    clone.style.top = '0';
    clone.style.left = '0';
    clone.style.width = '100%';
    clone.style.height = '100%';
    clone.style.objectFit = 'contain';
    clone.style.zIndex = '2';
    clone.style.pointerEvents = 'none';

    mediaBox.appendChild(clone);
    playing.set(slide, clone);
    clone.play().catch(function () {
      /* nothing to do - a muted play() call is never blocked in practice,
         but ignore silently if a browser ever refuses it. */
    });
  }

  function handleSlideChange(slide) {
    if (slide.classList.contains('is-selected')) {
      startVideo(slide);
    } else {
      stopVideo(slide);
    }
  }

  function init() {
    var slides = document.querySelectorAll('.product-main-slide');
    if (!slides.length) return;

    slides.forEach(function (slide) {
      // No tap-for-sound prompt on mobile - autoplay muted instead.
      hideTrigger(slide);
      handleSlideChange(slide);
    });

    // Flickity marks the active slide by toggling "is-selected" on it as
    // the shopper swipes; watch for that instead of re-implementing slide
    // tracking ourselves.
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          handleSlideChange(m.target);
        }
      });
    });
    slides.forEach(function (slide) {
      observer.observe(slide, { attributes: true, attributeFilter: ['class'] });
    });

    window.addEventListener('resize', function () {
      slides.forEach(function (slide) {
        if (isMobileWidth()) {
          handleSlideChange(slide);
        } else {
          stopVideo(slide);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Belt-and-braces: if a shopper ever taps the trigger before our script
  // hides it (e.g. a slow connection), still block the theme's fullscreen
  // sound modal from opening rather than letting the old jump happen.
  document.addEventListener('click', function (evt) {
    var trigger = evt.target.closest && evt.target.closest('.product-video-trigger--mp4');
    if (!trigger) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
  }, true);
})();
